import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import styled, { css, keyframes } from "styled-components";
import { keyboardFocus } from "../../common/keyboardFocus";
import { defaultControlSize } from "../newCommon/ControlsCommon";
import { Spinner } from "./Spinner";
export const defaultButtonType = "primary";
const _NewButton = forwardRef((props, ref) => {
    const { icon, rightIcon, children, buttonType: _, size: _a, loading, ...rest } = props;
    return (_jsxs("button", { ...rest, onClick: loading ? undefined : rest.onClick, ref: ref, children: [icon ? _jsx(ButtonIcon, { children: icon }) : null, children, rightIcon ? _jsx(RightIcon, { children: rightIcon }) : null, loading && (_jsx(LoaderWrapper, { children: _jsx(Spinner, {}) }))] }));
});
const LoaderWrapper = styled.span `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: inherit;
  opacity: 1;
`;
const ButtonIcon = styled.div ``;
const LeftIcon = styled(ButtonIcon) ``;
const RightIcon = styled(ButtonIcon) ``;
const NNewButton = styled(_NewButton).attrs(({ buttonType, size, theme: _t, loading, ...props }) => ({
    $buttonType: buttonType || defaultButtonType,
    $size: size || defaultControlSize,
    $loading: loading || false,
    ...props,
})) `
  font: inherit;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-weight: 700;
  border-width: 1px;
  border-style: solid;
  line-height: 1em;
  text-decoration: none;
  cursor: pointer;
  position: relative;
  outline: none !important;
  ${keyboardFocus};
  overflow: hidden;
  transition: all 0.2s ease-in-out;

  ${ButtonIcon} {
    flex-shrink: 0;
    margin-right: ${(p) => (p.buttonType === "icon" ? "0" : p.theme.spacing.sp8)};
    font-size: 16px;
  }

  ${RightIcon} {
    margin-left: ${(p) => (p.buttonType === "icon" ? "0" : p.theme.spacing.sp8)};
    margin-right: 0;
  }

  ${(p) => p.$buttonType === "icon" ? buttonIconSizeSchema[p.$size] : buttonSizeSchema[p.$size]};
  ${(p) => buttonTypeSchema[p.$buttonType]};
  ${(p) => buttonOverwrite(p.$size)[p.$buttonType]};

  & ${LoaderWrapper} {
    ${(p) => colorScheme[p.$buttonType]};
  }

  ${(p) => p.$loading &&
    css `
      *:not(${LoaderWrapper}, ${Spinner}) {
        opacity: 0;
      }

      color: rgba(0, 0, 0, 0);

      &:hover,
      &:focus,
      &:active,
      &:focus-visible {
        color: rgba(0, 0, 0, 0);
      }
    `};

  ${(p) => p.disabled &&
    css `
      cursor: not-allowed;
    `};

  ${(p) => p.$isPulsing &&
    css `
      overflow: visible;
      box-shadow: 0 0 0 0 rgba(10, 111, 232, 0.5);
      animation: ${pulsing} 1.5s linear infinite;
    `}
`;
export const NewButton = Object.assign(NNewButton, {
    ButtonIcon,
    LeftIcon,
    RightIcon,
    LoaderWrapper,
    Spinner,
});
const greyColor = css `
  color: ${({ theme }) => theme.systemColors.grey600};

  &:hover,
  &:focus,
  &:active,
  &:focus-visible {
    color: ${({ theme }) => theme.systemColors.grey600};
  }

  &:disabled {
    color: ${({ theme }) => theme.systemColors.grey300};
  }
`;
const blueColor = css `
  color: ${({ theme }) => theme.systemColors.blue600};

  &:hover,
  &:focus,
  &:active,
  &:focus-visible {
    color: ${({ theme }) => theme.systemColors.blue800};
  }

  &:disabled {
    color: ${({ theme }) => theme.systemColors.grey300};
  }
`;
const colorScheme = {
    primary: css `
    color: ${({ theme }) => theme.systemColors.white};

    &:hover,
    &:focus,
    &:active,
    &:focus-visible {
      color: ${({ theme }) => theme.systemColors.white};
    }

    &:disabled {
      color: ${({ theme }) => theme.systemColors.grey300};
    }
  `,
    icon: greyColor,
    cancel: css `
    color: ${({ theme }) => theme.systemColors.black};

    &:hover,
    &:focus,
    &:active,
    &:focus-visible {
      color: ${({ theme }) => theme.systemColors.black};
    }

    &:disabled {
      color: ${({ theme }) => theme.systemColors.grey300};
    }
  `,
    secondary: blueColor,
    tertiary: greyColor,
    text: blueColor,
};
const buttonTypeSchema = {
    primary: css `
    //State: default
    background: ${({ theme }) => theme.systemColors.blue600};
    border-color: ${({ theme }) => theme.systemColors.blue600};
    color: ${({ theme }) => theme.systemColors.white};
    //State: hover, focus
    &:hover,
    &:focus {
      background: ${({ theme }) => theme.systemColors.blue800};
      border-color: ${({ theme }) => theme.systemColors.blue800};
      color: ${({ theme }) => theme.systemColors.white};
    }

    //State: pressed
    &:active {
      background: ${({ theme }) => theme.systemColors.blue900};
      border-color: ${({ theme }) => theme.systemColors.blue900};
    }

    //State: keyboard focus
    &:focus-visible {
      background: ${({ theme }) => theme.systemColors.blue800};
      border-color: ${({ theme }) => theme.systemColors.blue800};
      color: ${({ theme }) => theme.systemColors.white};
    }

    //State: disabled
    &:disabled {
      background: ${({ theme }) => theme.systemColors.grey200};
      border-color: ${({ theme }) => theme.systemColors.grey200};
      color: ${({ theme }) => theme.systemColors.grey300};
    }
  `,
    secondary: css `
    //State: default
    background: transparent;
    color: ${({ theme }) => theme.systemColors.blue600};
    border-color: ${({ theme }) => theme.systemColors.blue600};
    border-width: 1px;
    //State: hover, focus
    &:hover,
    &:focus {
      border-color: ${({ theme }) => theme.systemColors.blue800};
      color: ${({ theme }) => theme.systemColors.blue800};
    }

    //State: pressed
    &:active {
      border-color: ${({ theme }) => theme.systemColors.blue900};
      color: ${({ theme }) => theme.systemColors.blue900};
    }

    //State: keyboard focus
    &:focus-visible {
      border-color: ${({ theme }) => theme.systemColors.blue800};
      color: ${({ theme }) => theme.systemColors.blue800};
    }

    //State: disabled
    &:disabled {
      border-color: ${({ theme }) => theme.systemColors.grey200};
      color: ${({ theme }) => theme.systemColors.grey300};
    }
  `,
    tertiary: css `
    //State: default
    background: ${({ theme }) => theme.systemColors.grey100};
    border-color: ${({ theme }) => theme.systemColors.grey100};
    color: ${({ theme }) => theme.systemColors.grey600};
    //State: hover, focus
    &:hover,
    &:focus {
      background: ${({ theme }) => theme.systemColors.grey200};
      border-color: ${({ theme }) => theme.systemColors.grey200};
    }

    //State: pressed
    &:active {
      background: ${({ theme }) => theme.systemColors.grey300};
      border-color: ${({ theme }) => theme.systemColors.grey300};
    }

    //State: keyboard focus
    &:focus-visible {
      background: ${({ theme }) => theme.systemColors.grey200};
      border-color: ${({ theme }) => theme.systemColors.grey200};
    }

    //State: disabled
    &:disabled {
      background: ${({ theme }) => theme.systemColors.grey200};
      border-color: ${({ theme }) => theme.systemColors.grey200};
      color: ${({ theme }) => theme.systemColors.grey300};
    }
  `,
    cancel: css `
    //State: default
    background: transparent;
    border-color: transparent;
    color: ${({ theme }) => theme.systemColors.black};
    //State: keyboard focus
    &:focus-visible {
      box-shadow: 0 0 0 2px ${({ theme }) => theme.systemColors.blue600};
    }

    //State: disabled
    &:disabled {
      color: ${({ theme }) => theme.systemColors.grey300};
    }
  `,
    text: css `
    //State: default
    background: transparent;
    border-color: transparent;
    color: ${({ theme }) => theme.systemColors.blue600};
    //State: hover, focus
    &:hover,
    &:focus {
      color: ${({ theme }) => theme.systemColors.blue800};
    }

    //State: pressed
    &:active {
      color: ${({ theme }) => theme.systemColors.blue900};
    }

    //State: keyboard focus
    &:focus-visible {
      color: ${({ theme }) => theme.systemColors.blue800};
      box-shadow: 0 0 0 2px ${({ theme }) => theme.systemColors.blue600};
    }

    //State: disabled
    &:disabled {
      color: ${({ theme }) => theme.systemColors.grey300};
    }
  `,
    icon: css `
    //State: default
    background: transparent;
    border-color: transparent;
    color: ${({ theme }) => theme.systemColors.grey600};
    padding: 0;
    display: flex;
    //State: hover, focus
    &:hover,
    &:focus {
      background: ${({ theme }) => theme.systemColors.grey200};
    }

    //State: pressed
    &:active {
      background: ${({ theme }) => theme.systemColors.grey300};
    }

    //State: keyboard focus
    &:focus-visible {
      background: ${({ theme }) => theme.systemColors.grey200};
    }

    //State: disabled
    &:disabled {
      color: ${({ theme }) => theme.systemColors.grey300};
      background: transparent;
    }
  `,
};
const buttonIconSizeSchema = {
    "50": css `
    height: 24px;
    width: 24px;
  `,
    "100": css `
    height: 32px;
    width: 32px;
  `,
    "200": css `
    height: 40px;
    width: 40px;

    ${ButtonIcon} {
      font-size: 20px;
    }
  `,
    "400": css `
    height: 40px;
    width: 40px;

    ${ButtonIcon} {
      font-size: 20px;
    }
  `,
};
const buttonSizeSchema = {
    "50": css `
    min-height: 24px;
    padding: 4px 6px;
    font-size: 12px;
  `,
    "100": css `
    min-height: 32px;
    padding: 6px 8px;
    font-size: 14px;
  `,
    "200": css `
    min-height: 40px;
    padding: 8px 16px;
    font-size: 16px;
  `,
    "400": css `
    min-height: 56px;
    padding: 8px 24px;
    font-size: 18px;

    ${ButtonIcon} {
      font-size: 20px;
    }
  `,
};
const buttonOverwrite = ($size) => ({
    cancel: css `
    ${buttonSizeSchemaOverwrite[$size]}
  `,
    text: css `
    ${buttonSizeSchemaOverwrite[$size]}
    padding-left: 0;
    padding-right: 0;
  `,
    icon: css ``,
    primary: css ``,
    secondary: css ``,
    tertiary: css ``,
});
const buttonSizeSchemaOverwrite = {
    "50": css `
    padding-left: 4px;
    padding-right: 4px;
  `,
    "100": css `
    padding-left: 4px;
    padding-right: 4px;
  `,
    "200": css `
    padding-left: 4px;
    padding-right: 4px;
  `,
    "400": css `
    padding-left: 8px;
    padding-right: 8px;
  `,
};
const pulsing = keyframes `
  0% {
    box-shadow: 0 0 0 0 rgba(10, 111, 232, 0.5);
  }
  70% {
    box-shadow: 0 0 0 16px rgba(10, 111, 232, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 111, 232, 0);
  }
`;
